/**
 * Gene Hub API client
 * Provides services for gene-disease association analysis
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

export interface TherapeuticArea {
  id: string;
  name: string;
  enabled: boolean;
  gene_count?: number;
}

export interface GeneHubDisease {
  id: string;
  name: string;
  description?: string;
  therapeutic_area: string;
}

export interface Gene {
  id: string;
  symbol: string;
  name: string;
  description?: string;
  therapeutic_areas: string[];
}

export interface MechanismInfo {
  id: string;
  title: string;
  summary: string;
  evidence_level: string;
  confidence_score: number;
}

export interface ExpressionData {
  tissue: string;
  expression_level: number;
  significance: number;
  percentile: number;
}

export interface AssociationResult {
  gene_id: string;
  disease: string;
  literature_summary: string;
  association_score: number;
  mechanisms: MechanismInfo[];
  expression_data: ExpressionData[];
  database_links: Record<string, string>;
  edge_predictions: {
    prediction_method?: string;
    confidence?: string;
    features_used?: string[];
    [key: string]: string | string[] | number | boolean | undefined;
  };
}

export interface AssociationAnalysisRequest {
  gene_id: string;
  disease_id?: string;
  custom_disease?: string;
  therapeutic_area: string;
}

export interface GeneSearchResult {
  genes: Gene[];
}

export interface GeneHubStats {
  total_genes: number;
  therapeutic_areas: number;
  enabled_tas: number;
  total_diseases: number;
  total_associations: number;
  last_updated: string;
}

class GeneHubClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async makeRequest<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get all available therapeutic areas
   */
  async getTherapeuticAreas(): Promise<TherapeuticArea[]> {
    return this.makeRequest<TherapeuticArea[]>('/api/gene-hub/therapeutic-areas');
  }

  /**
   * Get diseases for a specific therapeutic area
   */
  async getDiseasesByTA(therapeuticArea: string): Promise<GeneHubDisease[]> {
    return this.makeRequest<GeneHubDisease[]>(`/api/gene-hub/diseases/${encodeURIComponent(therapeuticArea)}`);
  }

  /**
   * Get genes filtered by therapeutic area
   */
  async getGenesByTA(therapeuticArea: string): Promise<Gene[]> {
    return this.makeRequest<Gene[]>(`/api/gene-hub/genes/${encodeURIComponent(therapeuticArea)}`);
  }

  /**
   * Search for genes by symbol or name
   */
  async searchGenes(query: string, therapeuticArea?: string, limit: number = 10): Promise<GeneSearchResult> {
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString(),
    });
    
    if (therapeuticArea) {
      params.append('therapeutic_area', therapeuticArea);
    }

    return this.makeRequest<GeneSearchResult>(`/api/gene-hub/gene-search?${params}`);
  }

  /**
   * Analyze gene-disease association
   */
  async analyzeAssociation(request: AssociationAnalysisRequest): Promise<AssociationResult> {
    return this.makeRequest<AssociationResult>('/api/gene-hub/analyze-association', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Get Gene Hub statistics
   */
  async getStats(): Promise<GeneHubStats> {
    return this.makeRequest<GeneHubStats>('/api/gene-hub/stats');
  }
}

// Create and export a default instance
export const geneHubClient = new GeneHubClient();

// Export the class for custom instances
export default GeneHubClient;